import { createApp } from '@backstage/frontend-defaults';
import appPlugin from '@backstage/plugin-app';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import spokeRequestsPlugin from '@internal/plugin-spoke-requests';
import spokeOverviewPlugin from '@internal/plugin-spoke-overview';
import roadmapPlugin from '@internal/plugin-roadmap';
import { navModule } from './modules/nav';
import { scaffolderCustomizations } from './scaffolder/scaffolderModule';

import {
  coreExtensionData,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  SignInPageProps,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import {
  configApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import LightIcon from '@material-ui/icons/WbSunny';
import { HOWDEN_THEMES, HowdenThemeProvider } from './themes/howdenTheme';
import { DataSphereLandingPage } from './modules/landing/DataSphereLandingPage';
import { DevPersonaSignInPage } from './modules/devPersona/DevPersonaSignInPage';
import './howdenStyles.css';
import { databaseAdminPlugin } from './modules/databaseAdmin/plugin';
import { homePlugin } from './modules/home/plugin';
import { observabilityPlugin } from './modules/observability/plugin';

// In e2e test mode (see app-config.e2e.yaml) fall back to the stock
// Backstage sign-in chrome with a guest provider, so Playwright can
// authenticate without a real Microsoft account — see
// e2e-tests/spoke-request-wizard.ado.e2e.ts, which clicks the default
// "Enter" guest button. Everywhere else, show the pretty DataSphere
// marketing landing page with a Microsoft sign-in call to action.
const HowdenSignInPage = (props: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  const e2eTestMode =
    configApi.getOptionalBoolean('scaffolder.e2eTestMode') ?? false;

  if (e2eTestMode) {
    return (
      <SignInPage
        {...props}
        providers={[
          'guest',
          {
            id: 'microsoft-auth-provider',
            title: 'Microsoft',
            message: 'Sign in with your Howden Microsoft account',
            apiRef: microsoftAuthApiRef,
          },
        ]}
      />
    );
  }

  if (configApi.getOptionalBoolean('datasphere.devPersonaSwitcher')) {
    return <DevPersonaSignInPage {...props} />;
  }

  return <DataSphereLandingPage {...props} />;
};

const microsoftSignInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: async () => HowdenSignInPage,
      },
    }),
  ],
});

// Every colour theme in the Howden digital brand guidelines (p10). Cobalt
// (id 'howden') is the default from app.appearance.theme. People can switch
// from the palette button in the top bar or Settings > Appearance.
const howdenThemes = HOWDEN_THEMES.map(spec =>
  ThemeBlueprint.make({
    name: spec.id,
    params: {
      theme: {
        id: spec.id,
        title: spec.title,
        variant: 'light',
        icon: <LightIcon />,
        Provider: ({ children }) => (
          <HowdenThemeProvider spec={spec}>{children}</HowdenThemeProvider>
        ),
      },
    },
  }),
);

const howdenThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: howdenThemes,
});

// The stock layout puts the nav in a left sidebar. DataSphere uses a top
// navigation bar instead, with the page below it.
const topNavLayoutModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    appPlugin.getExtension('app/layout').override({
      factory(_originalFactory, { inputs }) {
        return [
          coreExtensionData.reactElement(
            <div className="howden-shell">
              {inputs.nav.get(coreExtensionData.reactElement)}
              <div className="howden-shell-content">
                {inputs.content.get(coreExtensionData.reactElement)}
              </div>
            </div>,
          ),
        ];
      },
    }),
  ],
});

const disableDefaultThemesModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    appPlugin.getExtension('theme:app/light').override({ disabled: true }),
    appPlugin.getExtension('theme:app/dark').override({ disabled: true }),
  ],
});

export default createApp({
  features: [
    homePlugin,
    observabilityPlugin,
    catalogPlugin,
    spokeRequestsPlugin,
    spokeOverviewPlugin,
    roadmapPlugin,
    databaseAdminPlugin,
    navModule,
    disableDefaultThemesModule,
    topNavLayoutModule,
    howdenThemeModule,
    scaffolderCustomizations,
    microsoftSignInModule,
  ],
});
